import { useEffect, useMemo, useState } from 'react';
import {
  getAdminTierConfig,
  upsertAdminTierDefinition,
  updateAdminWeeklyConfig,
  type AdminTierConfigResponse,
  type WireTierDefinition,
} from '../api/endpoints.js';
import { useApiMutation, useApiQuery } from '../api/hooks.js';
import { useCan } from '../session/can.js';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Field,
  Icon,
  Input,
  LoadingState,
  PageHead,
  Select,
  Switch,
  TBody,
  TD,
  THead,
  TH,
  TRow,
  Table,
} from '../ui/index.js';
import { SoftBreaks } from './user-inspect/SoftBreaks.js';
import '../styles/pricing.mobile.css';

const DOW_LABELS = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
const COMMON_TZ = ['UTC', 'Europe/Istanbul', 'Europe/London', 'America/New_York', 'Asia/Tokyo'];

/**
 * `/tier-config` — Seviye Ayarları. Two independent admin-configurable tier
 * ladders (Kalıcı / Haftalık) + the weekly period (start day + timezone).
 * Read gated on `tier:view`, writes on `tier:manage`. The api caches these
 * tables and refreshes out-of-band, so a save takes effect within ~60s on the
 * order path.
 */
export function TierConfigPage(): JSX.Element {
  const canView = useCan('tier:view');
  const canManage = useCan('tier:manage');
  const query = useApiQuery<AdminTierConfigResponse>((o) => getAdminTierConfig(o), []);

  if (!canView) {
    return (
      <div className="tier-page">
        <PageHead title="Seviye Ayarları" />
        <ErrorState reason="capability_missing:tier:view" />
      </div>
    );
  }

  return (
    <div className="tier-page">
      <PageHead
        crumb={<>Sistem <Icon.ChevronRight size={11} /> Seviye Ayarları</>}
        title="Seviye Ayarları"
        subtitle="İki bağımsız seviye sistemi: Kalıcı Seviye (toplam yükleme) + Haftalık Seviye (hafta içi yükleme). Uygulanan indirim ikisinin TOPLAMIDIR (birleşir, en fazla %50). İndirim oranları sabittir (%0'dan %25'e, kademe başına %5) — yalnız eşik, ad ve aktiflik düzenlenebilir."
        actions={
          <Button variant="secondary" icon={<Icon.Refresh size={14} />} onClick={() => query.reload()}>
            Yenile
          </Button>
        }
      />
      {query.kind === 'loading' ? <LoadingState /> : null}
      {query.kind === 'error' ? <ErrorState reason={query.reason} onRetry={query.reload} /> : null}
      {query.kind === 'ok' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <TierLadder
            title="Kalıcı Seviye (Lifetime)"
            scope="lifetime"
            tiers={query.data.tiers.filter((t) => t.scope === 'lifetime')}
            canManage={canManage}
            onSaved={() => query.reload()}
          />
          <TierLadder
            title="Haftalık Seviye (Weekly)"
            scope="weekly"
            tiers={query.data.tiers.filter((t) => t.scope === 'weekly')}
            canManage={canManage}
            onSaved={() => query.reload()}
          />
          <WeeklyPeriodCard
            startDow={query.data.weekly.startDow}
            timezone={query.data.weekly.timezone}
            canManage={canManage}
            onSaved={() => query.reload()}
          />
        </div>
      ) : null}
    </div>
  );
}

interface TierDraft {
  name: string;
  minUsd: string;
  isActive: boolean;
}

function toDraft(t: WireTierDefinition): TierDraft {
  return {
    name: t.name,
    minUsd: (t.minTopupMicro / 1_000_000).toString(),
    isActive: t.isActive,
  };
}

function TierLadder({
  title,
  scope,
  tiers,
  canManage,
  onSaved,
}: {
  readonly title: string;
  readonly scope: 'lifetime' | 'weekly';
  readonly tiers: readonly WireTierDefinition[];
  readonly canManage: boolean;
  readonly onSaved: () => void;
}): JSX.Element {
  const sorted = useMemo(() => [...tiers].sort((a, b) => a.tierIndex - b.tierIndex), [tiers]);
  return (
    <Card flush>
      <CardHeader title={title} />
      <CardBody tight style={{ padding: 0 }}>
        {/* Phones (≤720): each tier becomes a stacked card (.tbl-stack,
            styles.css §10); the desktop table is unchanged. */}
        <Table stack className="tier-tbl">
          <THead>
            <TRow>
              <TH>Kademe</TH>
              <TH>Ad</TH>
              <TH>Min. yükleme ($)</TH>
              <TH>İndirim (sabit)</TH>
              <TH>Aktif</TH>
              <TH></TH>
            </TRow>
          </THead>
          <TBody>
            {sorted.map((t) => (
              <TierRowEditor key={`${scope}:${t.tierIndex}`} tier={t} canManage={canManage} onSaved={onSaved} />
            ))}
          </TBody>
        </Table>
      </CardBody>
    </Card>
  );
}

function TierRowEditor({
  tier,
  canManage,
  onSaved,
}: {
  readonly tier: WireTierDefinition;
  readonly canManage: boolean;
  readonly onSaved: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState<TierDraft>(() => toDraft(tier));
  // Re-sync the draft when the upstream value changes (after a reload).
  useEffect(() => setDraft(toDraft(tier)), [tier]);

  const mut = useApiMutation<null, { tier: WireTierDefinition }>((opts) => {
    const minUsd = Number(draft.minUsd);
    return upsertAdminTierDefinition(opts, tier.scope, tier.tierIndex, {
      name: draft.name.trim(),
      // Tier 1 is the floor — the backend forces min 0 regardless. The discount
      // rate is operator-fixed (tier×5%) and the description is derived on the
      // customer page; neither is editable here.
      minTopupMicro: Math.max(0, Math.round(minUsd * 1_000_000)),
      isActive: draft.isActive,
    });
  });

  async function save(): Promise<void> {
    if (!canManage) return;
    const res = await mut.run(null);
    if (res.kind === 'ok') onSaved();
  }

  return (
    <>
      <TRow>
        <TD className="t-mono t-num tbl-stack-primary tier-index-cell" label="Kademe">{tier.tierIndex}</TD>
        <TD className="tbl-stack-full tier-name-cell" label="Ad">
          <Input className="tier-name-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} disabled={!canManage} />
        </TD>
        <TD className="tbl-stack-full" label="Min. yükleme ($)">
          <Input
            className="tier-min-input"
            type="number"
            min={0}
            step="0.01"
            value={draft.minUsd}
            onChange={(e) => setDraft({ ...draft, minUsd: e.target.value })}
            disabled={!canManage || tier.tierIndex === 1}
          />
        </TD>
        <TD className="tbl-stack-status tier-discount-cell" label="İndirim (sabit)">
          {/* Discount is operator-fixed at tier×5% — display only, not editable. */}
          <Badge tone="neutral">%{(tier.discountBps / 100).toFixed(0)}</Badge>
        </TD>
        <TD className="tier-active-cell" label="Aktif">
          <Switch checked={draft.isActive} onChange={(v) => setDraft({ ...draft, isActive: v })} disabled={!canManage} />
        </TD>
        <TD className="text-right tbl-stack-actions">
          <Button variant="primary" size="sm" disabled={!canManage} loading={mut.state.kind === 'running'} onClick={() => void save()}>
            Kaydet
          </Button>
        </TD>
      </TRow>
      {mut.state.kind === 'error' ? (
        <TRow className="tier-error-row">
          <TD colSpan={6}><Banner tone="danger"><SoftBreaks text={mut.state.reason} /></Banner></TD>
        </TRow>
      ) : null}
    </>
  );
}

function WeeklyPeriodCard({
  startDow,
  timezone,
  canManage,
  onSaved,
}: {
  readonly startDow: number;
  readonly timezone: string;
  readonly canManage: boolean;
  readonly onSaved: () => void;
}): JSX.Element {
  const [dow, setDow] = useState(String(startDow));
  const [tz, setTz] = useState(timezone);
  useEffect(() => {
    setDow(String(startDow));
    setTz(timezone);
  }, [startDow, timezone]);

  const mut = useApiMutation<null, { weekly: { startDow: number; timezone: string } }>((opts) =>
    updateAdminWeeklyConfig(opts, { startDow: Number(dow), timezone: tz.trim() }),
  );
  async function save(): Promise<void> {
    if (!canManage) return;
    const res = await mut.run(null);
    if (res.kind === 'ok') onSaved();
  }
  const tzKnown = COMMON_TZ.includes(tz);

  return (
    <Card flush>
      <CardHeader
        title="Haftalık Dönem"
        actions={<Badge tone="neutral" dot>{DOW_LABELS[startDow] ?? startDow} · {timezone}</Badge>}
      />
      <CardBody>
        <div className="tier-weekly-form">
          <Field label="Hafta başlangıç günü" htmlFor="dow">
            <Select id="dow" className="tier-dow-select" value={dow} onChange={(e) => setDow(e.target.value)} disabled={!canManage}>
              {DOW_LABELS.map((label, i) => (
                <option key={i} value={String(i)}>{label}</option>
              ))}
            </Select>
          </Field>
          <Field label="Zaman dilimi (IANA)" htmlFor="tz">
            <Select
              id="tz"
              className="tier-tz-select"
              value={tzKnown ? tz : '__custom'}
              onChange={(e) => {
                if (e.target.value !== '__custom') setTz(e.target.value);
              }}
              disabled={!canManage}
            >
              {COMMON_TZ.map((z) => (
                <option key={z} value={z}>{z}</option>
              ))}
              {!tzKnown ? <option value="__custom">{tz} (özel)</option> : null}
            </Select>
          </Field>
          <Field label="Özel zaman dilimi" htmlFor="tzc">
            <Input id="tzc" className="tier-tz-input" value={tz} onChange={(e) => setTz(e.target.value)} disabled={!canManage} placeholder="e.g. Europe/Istanbul" />
          </Field>
          <Button variant="primary" disabled={!canManage} loading={mut.state.kind === 'running'} onClick={() => void save()}>
            Kaydet
          </Button>
        </div>
        <div className="t-xs t-tertiary" style={{ marginTop: 8 }}>
          Haftalık seviye bu güne/zaman dilimine göre sıfırlanır. Sıfırlama tembeldir (cron yok): bir kullanıcının
          haftalık toplamı, kayıtlı hafta artık güncel değilse otomatik 0 sayılır.
        </div>
        {mut.state.kind === 'error' ? (
          <div style={{ marginTop: 8 }}><Banner tone="danger"><SoftBreaks text={mut.state.reason} /></Banner></div>
        ) : null}
      </CardBody>
    </Card>
  );
}
